// src/utils/scoring.js
import domains from '../data/domains';
import preferences from '../data/preferences';
import constellations from '../data/constellations';
import { loadScoring as loadScoringConfig } from './assessmentItemLoader';

// Map option labels to sub-format for weighting selection
function inferSubFormatFromLabels(options = []) {
	const labels = options.map(o => o.label);
	if (labels.includes('Never') && labels.includes('Very often')) return 'freq';
	if (labels.includes('Not at all') && labels.includes('Extremely')) return 'conf';
	return 'likert';
}

function stripStagePrefix(questionId) {
	return questionId.replace(/^s[12]_/, '');
}

function getStageFromQuestionId(questionId) {
	return questionId.startsWith('s2_') ? 2 : 1;
}

function buildReverseLookup(scoringConfig) {
	// Returns a Set of itemIds that are reverse keyed and a map of elementId->formatWeights
	const reverseSet = new Set();
	const elementWeights = {};
	Object.entries(scoringConfig).forEach(([elementId, cfg]) => {
		(cfg.reverseItemIds || []).forEach(id => reverseSet.add(id));
		elementWeights[elementId] = cfg.formatWeights || { likert: 1.0, freq: 1.0, conf: 1.0, forced: 1.2 };
	});
	return { reverseSet, elementWeights };
}

// Calculate element-level scores from responses and authored question bank
export const calculatePreferenceScores = (responses, questionBank) => {
	// Aggregators per elementId
	const elementAgg = {};

	// Prepare reverse lookups for both stages
	const stage1 = loadScoringConfig(1);
	const stage2 = loadScoringConfig(2);
	const { reverseSet: rev1, elementWeights: weights1 } = buildReverseLookup(stage1);
	const { reverseSet: rev2, elementWeights: weights2 } = buildReverseLookup(stage2);

	// Helper to init element aggregator entry
	function ensureElement(elementId, domainId) {
		if (!elementAgg[elementId]) {
			const prefMeta = preferences.getPreferenceById(elementId) || { id: elementId, name: elementId, descriptor: '', description: '' };
			elementAgg[elementId] = {
				id: elementId,
				name: prefMeta.name || elementId,
				descriptor: prefMeta.descriptor || '',
				description: prefMeta.description || '',
				domainId: domainId || prefMeta.domainId || null,
				points: 0,
				maxPoints: 0,
				forcedWins: 0,
				forcedCount: 0,
				score: 0
			};
		}
	}

	// Iterate all answered questions
	Object.entries(responses).forEach(([qid, answer]) => {
		const q = questionBank.find(x => x.id === qid);
		if (!q) return;
		const stage = getStageFromQuestionId(qid);
		const originalItemId = stripStagePrefix(qid);
		const weights = stage === 2 ? weights2 : weights1;
		const reverseSet = stage === 2 ? rev2 : rev1;

		// Forced-choice: answer is elementId of selection
		if (q.type === 'forcedChoice') {
			const selectedElementId = typeof answer === 'string' ? answer : null;
			if (selectedElementId) {
				ensureElement(selectedElementId, q.domainId);
				const w = (weights[selectedElementId]?.forced) ?? 1.2;
				elementAgg[selectedElementId].forcedWins += 1 * w;
				elementAgg[selectedElementId].forcedCount += 1 * w;
			}
			return;
		}

		// Likert-like
		if (q.type === 'likert') {
			const elementId = q.elementId;
			ensureElement(elementId, q.domainId);
			const raw = parseInt(answer, 10);
			if (!Number.isFinite(raw)) return;
			const base = Math.max(1, Math.min(5, raw)) - 1; // 0..4
			const isRev = reverseSet.has(originalItemId);
			const subFormat = inferSubFormatFromLabels(q.options);
			const w = (weights[elementId]?.[subFormat]) ?? 1.0;
			const val = isRev ? (4 - base) : base; // still 0..4
			elementAgg[elementId].points += val * w;
			elementAgg[elementId].maxPoints += 4 * w;
		}
	});

	// Compute final 0..100 scores per element
	Object.values(elementAgg).forEach(e => {
		const likertPart = e.maxPoints > 0 ? (e.points / e.maxPoints) : 0; // 0..1
		const forcedPart = e.forcedCount > 0 ? (e.forcedWins / e.forcedCount) : 0; // already weighted proportion 0..1
		// Combine: average the available parts (if both exist), else use the one present
		let combined = 0;
		let parts = 0;
		if (e.maxPoints > 0) { combined += likertPart; parts += 1; }
		if (e.forcedCount > 0) { combined += forcedPart; parts += 1; }
		e.score = Math.round(((parts > 0 ? combined / parts : 0) * 100));
	});

	return elementAgg;
};

export const calculateDomainScores = (preferenceScores) => {
	const domainScores = {};
	const counts = {};
	domains.forEach(d => { domainScores[d.id] = 0; counts[d.id] = 0; });
	Object.values(preferenceScores).forEach(e => {
		const d = e.domainId;
		if (!d || domainScores[d] === undefined) return;
		domainScores[d] += e.score;
		counts[d] += 1;
	});
	Object.keys(domainScores).forEach(d => {
		if (counts[d] > 0) domainScores[d] = Math.round(domainScores[d] / counts[d]);
	});
	return domainScores;
};

export const getTopPreferences = (preferenceScores, count = 8) => {
	return Object.values(preferenceScores)
		.sort((a, b) => b.score - a.score)
		.slice(0, count);
};

export const getTopDomains = (domainScores) => {
	return Object.entries(domainScores)
		.sort((a, b) => b[1] - a[1])
		.map(([id]) => id);
};

/**
 * The constellation is the client's top two domains, unconditionally.
 *
 * -------------------------------------------------------------------------
 * THE 15-POINT SIGNIFICANCE BRANCH WAS DELETED 2026-09-14, ON DAN'S CALL
 * -------------------------------------------------------------------------
 * This function used to refuse the pair when the top domain led the second by
 * more than 15 points, and fall back to `constellations.find(c => c.domains[0]
 * === topDomain)`. Three measurements retired it, taken over the ten real
 * de-identified profiles in `apc-crm`'s E.1 fixtures:
 *
 * 1. IT NEVER REACHED A SCREEN. `ResultsProcessor.transform()` runs on every
 *    path a person can open their results by and overwrites `constellationData`
 *    with the top-two pairing. So the rendered archetype has always been the
 *    pairing, in this app and in the CRM's coach-facing markdown alike.
 *
 * 2. IT DID NOT EXPRESS DOMINANCE. The fallback returns the FIRST catalog entry
 *    whose primary domain matches, so it names a two-domain archetype anyway
 *    with the second domain chosen by catalog array order. A client leading on
 *    insight by 25 points was told they were a Luminary, "insight and vision",
 *    even where vision was the domain they scored lowest on. The synthetic
 *    `custom` result was unreachable, because all ten domain pairs exist.
 *
 * 3. ON CORRECTED NUMBERS IT WOULD FIRE ON NOBODY. Since the domain arithmetic
 *    was corrected the largest real top-two gap is 9 points, against 16 under
 *    the old inflation. A threshold calibrated on numbers that no longer exist
 *    is dead code that would wake up wrongly the next time the arithmetic moves.
 *
 * Domain scores are means of element percentiles inside a group, so they
 * compress: on four of ten real profiles the second and third domains sit within
 * two points. The archetype is therefore a LIGHT SUMMARY rather than a precise
 * identity claim, and where dominance or closeness matters it belongs in
 * evidence-aware language, never in swapping which archetype gets named.
 *
 * Full brief: JDH-Second-Brain `projects/client-portal/
 * 2026-09-14-career-constellation-archetype-decision-brief.md`.
 *
 * RESIDUAL, RECORDED AND DEFERRED: a tie resolves by insertion order, which is
 * element rank order here and a fixed domain list in the CRM, so identical
 * numbers could still name different archetypes. One real profile ties for
 * first place. Both products agree on all ten today. A principled tiebreak
 * needs unrounded evidence on both sides, so it waits for a reason.
 */
export const determineConstellation = (topDomains, domainScores) => {
	const primaryDomains = topDomains.slice(0, 2);
	const [first, second] = primaryDomains;
	const matchedConstellation = second
		? constellations.find(c => c.domains.includes(first) && c.domains.includes(second))
		: constellations.find(c => c.domains.includes(first));
	if (matchedConstellation) return matchedConstellation;
	return {
		id: 'custom',
		name: 'Custom',
		domains: primaryDomains,
		description: 'Your unique pattern of preferences forms a custom constellation that reflects your individual approach to work.',
		careerDirections: []
	};
};

export const processAssessmentResults = (responses, questionBank) => {
	const preferenceScores = calculatePreferenceScores(responses, questionBank);
	const topPreferences = getTopPreferences(preferenceScores);
	const domainScores = calculateDomainScores(preferenceScores);
	const topDomains = getTopDomains(domainScores);
	const constellationData = determineConstellation(topDomains, domainScores);
	return {
		preferenceScores,
		topPreferences: topPreferences.map(p => p.id),
		domainScores,
		topDomains,
		constellationData,
		constellation: constellationData.id,
		timestamp: new Date().toISOString()
	};
};
